import {ref} from 'vue'
import firebase from '../firebase'
import type { AICollabMessage, AIUser } from '@/types'
import axios from 'axios'
import { store } from '@/store'
import { useStore } from 'vuex'
import type { AICollabProject } from '@/types'
// Define message types
export interface ChatMessage {
    id: string;
    content: string;
    type: 'human' | 'ai';
    timestamp: Date | firebase.firestore.Timestamp;
    [key: string]: any; // For any additional properties
}

// Production URL 
const baseUrl = "https://ai.dansuk.com.tr"

// Local URL
//const baseUrl = "http://localhost:8000"


// Individual Messaging With AI

// Get a list of all sessions

export const useGetAllSessions = (userId: string) => {
    const sessions = ref([])
    const isLoadingSessions = ref(false)
    const store = useStore()

    const getSessions = async () => {
        sessions.value = []
        isLoadingSessions.value = true
        const sessionPath = `ai/personal/users/${userId}/sessions`
        const sessionRef = firebase.firestore().collection(sessionPath)
        const sessionSnapshot = await sessionRef.orderBy('created_at', 'desc').get()
        sessionSnapshot.docs.forEach((doc) => {
            sessions.value.push({
                id: doc.id,
                ...doc.data()
            })
        })

        if(sessions.value.length === 1){ 
            // If there's only one session, set it as the selected session
            store.dispatch('ai/setSelectedSession', sessions.value[0])
        }

        isLoadingSessions.value = false         
        console.log(sessions.value)
    }

    getSessions()
    return {
        sessions,
        isLoadingSessions,
        getSessions
    }
}

// Get messages in a session

export const useGetChatMessages = (sessionId: string, userId: string) => {
    const chatMessages = ref<ChatMessage[]>([])
    const isLoadingChatMessages = ref(false)
    let unsubscribe: (() => void) | null = null
    const aiResponseReceived = ref(false)

    const getChatMessages = () => {
        isLoadingChatMessages.value = true
        aiResponseReceived.value = false
        
        const messagePath = `ai/personal/users/${userId}/sessions/${sessionId}/messages`
        const messageRef = firebase.firestore().collection(messagePath)
        
        // Set up real-time listener with ordering by timestamp in ascending order
        unsubscribe = messageRef
            .orderBy('timestamp', 'asc')
            .onSnapshot((snapshot) => {
                let hasNewAIMessage = false;
                
                snapshot.docChanges().forEach((change) => {
                    if (change.type === 'added') {
                        const newMessage = {
                            id: change.doc.id,
                            ...change.doc.data()
                        } as ChatMessage;
                        
                        // Check if this is a user message that might replace a temporary one
                        if (newMessage.type === 'human') {
                            // Find any temporary message with the same content
                            const tempIndex = chatMessages.value.findIndex(msg => 
                                msg.id.startsWith('temp-') && msg.content === newMessage.content
                            );
                            
                            if (tempIndex !== -1) {
                                // Replace the temporary message with the real one
                                chatMessages.value[tempIndex] = newMessage;
                            } else {
                                // If no temporary message found, just add the new one
                                chatMessages.value.push(newMessage);
                            }
                        } else if (newMessage.type === 'ai') {
                            // For AI messages, just add them
                            chatMessages.value.push(newMessage);
                            hasNewAIMessage = true;
                        }
                    }
                });
                
                // If we received a new AI message, update the aiResponseReceived ref
                if (hasNewAIMessage) {
                    aiResponseReceived.value = true;
                }
                
            }, (error) => {
                console.error("Error listening to chat messages:", error)
            })
            isLoadingChatMessages.value = false
    }

    // Start listening
    getChatMessages()
    
    // Return cleanup function to unsubscribe when component is unmounted
    const cleanup = () => {
        if (unsubscribe) {
            unsubscribe()
            unsubscribe = null
        }
    }

    return {
        chatMessages,
        isLoadingChatMessages,
        getChatMessages,
        cleanup,
        aiResponseReceived
    }
}

export const createNewSession = async (userId: string) => {
    const sessionPath = `ai/personal/users/${userId}/sessions`

    const sessionName = `Yeni Konuşma - ${new Date().toLocaleDateString("tr-TR", {
        day: "numeric",
        month: "short",
        year: "numeric"
    })}`

    // Create a new session
    const sessionCollectionRef = firebase.firestore().collection(sessionPath)
    const newSessionRef = await sessionCollectionRef.add({
        name: sessionName,
        created_at: new Date(),
    })

    // Get the whole session data that was created
    const sessionRef = firebase.firestore().collection(sessionPath).doc(newSessionRef.id)
    const sessionSnapshot = await sessionRef.get()
    const sessionData = {
        id: newSessionRef.id,
        ...sessionSnapshot.data()
    }


    return sessionData
    
}

export const postChatMessage = async (message: string, sessionId: string, user: AIUser) => {
    
    const messageData = {
        message: message,
        session_id: sessionId,
        user: user
    }
    
    const response = await axios.post(baseUrl + "/chat", messageData)
    return response.data
}

// Collective Messaging With AI

// Get all projects that the user is allowed to access

export const useGetAllProjects = (userId: string) => {
    const projects = ref([])
    const isLoadingProjects = ref(false)

    const getProjects = async () => {
        projects.value = []
        isLoadingProjects.value = true

        // TODO - Implement a check to see if the user is allowed to access the project.

        const userProjects= []
        const userPath = `team/${userId}/aiProjects`
        const userRef = firebase.firestore().collection(userPath)
        const userSnapshot = await userRef.get()
        userSnapshot.docs.forEach((doc) => {
            userProjects.push({
                id: doc.id,
                ...doc.data()
            })
        })

        console.log("User Projects: ", userProjects)
        
        // Get all the projects that the user is a member of
        const projectPath = `ai/collective/projects`
       
        for (const project of userProjects){
            const projectRef = firebase.firestore().collection(projectPath).doc(project.id)
            const projectSnapshot = await projectRef.get()
            projects.value.push({
                id: projectSnapshot.id,
                ...projectSnapshot.data()
            })
        }
        
        

        console.log("All Projects: ", projects.value)
        if(projects.value.length === 1){ 
            // If there's only one project, set it as the selected project
            store.dispatch('ai/setSelectedProject', projects.value[0])
        }

        isLoadingProjects.value = false
    }
    
    getProjects()

    return {
        projects,
        isLoadingProjects,
        getProjects
    }
}

export const createNewProject = async (project: AICollabProject) => {

    if(project.infoBase){
        project["isProcessingInfoBase"] = true
    }

    try{
        const projectPath = `ai/collective/projects`
        const projectRef = firebase.firestore().collection(projectPath)
        const newProjectRef = await projectRef.add(project)
        return newProjectRef.id
    } catch (error) {
        console.error("Error creating new project:", error)
        return false
    }


}

export const assignProjectToUser = async (projectId: string, userId: string) => {
   try{
        const usersPath = `team/${userId}/aiProjects`
        const usersRef = firebase.firestore().collection(usersPath)
        await usersRef.doc(projectId).set({
            role: "member",
            addedOn: new Date(),
        })
   } catch (error) {
    console.error("Error assigning project to user:", error)
    return false
   }
 
}

// Get the messages in a project

export const useGetAllProjectMessages = (projectId: string) => {
    const messages = ref<AICollabMessage[]>([])
    const isLoadingMessages = ref(false)
    let unsubscribe: (() => void) | null = null
    const aiResponseReceived = ref(false)

    const getMessages = () => {
        isLoadingMessages.value = true
        aiResponseReceived.value = false
        
        const messagePath = `ai/collective/projects/${projectId}/messages`
        const messageRef = firebase.firestore().collection(messagePath)
        
        // Set up real-time listener with ordering by timestamp in ascending order
        unsubscribe = messageRef
            .orderBy('timestamp', 'asc')
            .onSnapshot((snapshot) => {
                let hasNewAIMessage = false;
                
                snapshot.docChanges().forEach((change) => {
                    if (change.type === 'added') {
                        const newMessage = {
                            id: change.doc.id,
                            ...change.doc.data()
                        } as AICollabMessage;
                        
                        // Check if this is a user message that might replace a temporary one
                        if (newMessage.type === 'human') {
                            // Find any temporary message with the same content
                            const tempIndex = messages.value.findIndex(msg => 
                                msg.id && msg.id.startsWith('temp-') && msg.content === newMessage.content
                            );
                            
                            if (tempIndex !== -1) {
                                // Replace the temporary message with the real one
                                messages.value[tempIndex] = newMessage;
                            } else {
                                // If no temporary message found, just add the new one
                                messages.value.push(newMessage);
                            }
                        } else if (newMessage.type === 'ai') {
                            // For AI messages, just add them
                            messages.value.push(newMessage);
                            hasNewAIMessage = true;
                        }
                    }
                });
                
                // If we received a new AI message, update the aiResponseReceived ref
                if (hasNewAIMessage) {
                    aiResponseReceived.value = true;
                }
                
            }, (error) => {
                console.error("Error listening to project messages:", error)
            })
            isLoadingMessages.value = false
    }

    // Start listening
    getMessages()
    
    // Return cleanup function to unsubscribe when component is unmounted
    const cleanup = () => {
        if (unsubscribe) {
            unsubscribe()
            unsubscribe = null
        }
    }

    return {
        messages,
        isLoadingMessages,
        getMessages,
        cleanup,
        aiResponseReceived
    }
}

export const postProjectMessage = async (message: string, projectId: string, user: AIUser) => {
    const messageData = {
        message: message,
        project_id: projectId,
        user: user
    }

    const response = await axios.post(baseUrl + "/project", messageData)

    if(response.status === 200){
        return response.data
    } else {
        console.error("Error posting project message:", response.data)
        return false
    }

}

export const processAIProject = async (projectId: string) => {
    const response = await axios.post(baseUrl + "/process", {
        project_id: projectId
    })
    return response.data
}
